/**
 * One clinic: its members and their roles, the patients linked to it with moving a link to another
 * clinic or removing it (ADM-003), and the history of those changes. A removed link stays listed as
 * removed, since the Worker revokes links rather than deleting them.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminOrganizationDetail, AdminOrganizationRow, AdminPatientLink } from "@lattice/core";
import { ROUTES } from "@lattice/core";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import type { Me } from "./api.ts";
import { api } from "./api.ts";
import { ClinicPicker } from "./ClinicPicker.tsx";
import { Context, Info, Person, RecordLink, Truncate } from "./bits.tsx";
import { FIELD_INFO } from "./staff.ts";
import type { Column } from "./layout.tsx";
import { History, PageHead, Panel, Section, Split, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { ReasonForm } from "./ReasonForm.tsx";
import { href } from "./router.ts";
import { BackToResults, Button, ErrorText, Status } from "./ui.tsx";
import { CLINIC_ROLE, day, refusal } from "./words.ts";
const STATUS = {
  active: <Status tone="ok">Active</Status>,
  pending: <Status tone="warn">Pending</Status>,
  revoked: <Status tone="neutral">Removed</Status>
};
const SCOPE = {
  clinic_created: "Records the clinic made",
  all: "Everything"
};
type Change = {
  readonly kind: "move" | "remove";
  readonly link: AdminPatientLink;
};
function ChangePanel({
  change,
  clinicId,
  onDone,
  onCancel
}: {
  readonly change: Change;
  readonly clinicId: string;
  readonly onDone: (next: AdminOrganizationDetail) => void;
  readonly onCancel: () => void;
}) {
  const [destination, setDestination] = useState<AdminOrganizationRow | null>(null);
  const panel = useRef<HTMLDivElement>(null);

  /* The panel opens below the whole table, which on a long list is off screen from the row. */
  useEffect(() => {
    panel.current?.scrollIntoView({
      block: "nearest"
    });
    panel.current?.querySelector<HTMLElement>("input, textarea")?.focus({
      preventScroll: true
    });
  }, []);
  const name = change.link.patientName ?? "this patient";
  const moving = change.kind === "move";
  const practitionerNote = change.link.practitionerName === null ? "" : ` ${change.link.practitionerName} stops being the practitioner, since they belong to this clinic.`;
  async function submit(reason: string): Promise<string | null> {
    const result = moving ? await api<AdminOrganizationDetail>(ROUTES.adminMoveLink, {
      params: {
        linkId: change.link.id
      },
      body: {
        organizationId: destination?.id ?? "",
        reason
      }
    }) : await api<AdminOrganizationDetail>(ROUTES.adminRemoveLink, {
      params: {
        linkId: change.link.id
      },
      body: {
        reason
      }
    });
    if (!result.ok) return refusal(result.error);

    /* A move answers with the destination clinic, so this page reloads its own. */
    if (!moving) {
      onDone(result.data);
      return null;
    }
    const here = await api<AdminOrganizationDetail>(ROUTES.adminOrganization, {
      params: {
        organizationId: clinicId
      }
    });
    if (here.ok) onDone(here.data);
    return null;
  }
  return <Panel padded>
      <div ref={panel} className="x78zum5 xdt5ytf x1r0x45p x27kpxv">
        <h3 className="x1ghz6dp x1rf3zrh x117lpx5 x1e5flnj">
          {moving ? `Move ${name} to another clinic` : `Remove ${name} from this clinic`}
        </h3>
        <p className="x1ghz6dp x1jty25b">
          {moving ? `The link keeps its sharing and status.${practitionerNote} Nobody is emailed.` : "This clinic loses access to the patient's records at once. The link stays listed here as removed. Nobody is emailed."}
        </p>
        <ReasonForm id={`${change.kind}-reason`} hint="Logged with your name. Include the ticket number." action={moving ? "Move link" : "Remove link"} tone={moving ? "primary" : "danger"} onSubmit={submit} onCancel={onCancel} ready={!moving || destination !== null}>
          {moving ? <ClinicPicker exclude={clinicId} chosen={destination} onChoose={setDestination} /> : null}
        </ReasonForm>
      </div>
    </Panel>;
}
function linkColumns(canChange: boolean, choose: (change: Change) => void): readonly Column<AdminPatientLink>[] {
  const columns: Column<AdminPatientLink>[] = [{
    label: "Patient",
    nowrap: true,
    cell: l => <RecordLink to={href.patient(l.patientId)} relation="Patient" label={l.patientName ?? "Name not given"} />
  }, {
    label: "Status",
    nowrap: true,
    cell: l => STATUS[l.status]
  }, {
    label: "Shares",
    phoneLabel: true,
    cell: l => <span>
          {SCOPE[l.shareScope]} <Info label="Share scope" text={FIELD_INFO.scope} />
        </span>
  }, {
    label: "Practitioner",
    nowrap: true,
    phoneLabel: true,
    cell: l => l.practitionerName ?? <span className="x1jty25b">None</span>
  }, {
    label: "Linked",
    numeric: true,
    phoneLabel: true,
    cell: l => day(l.createdAt)
  }];
  if (!canChange) return columns;
  return [...columns, {
    label: "Actions",
    numeric: true,
    wide: true,
    cell: l => l.status === "revoked" ? null : <div className="x78zum5 x1a02dak x1173whf">
            <Button tone="quiet" onClick={() => choose({
        kind: "move",
        link: l
      })}>
              Move
            </Button>
            <Button tone="quietDanger" onClick={() => choose({
        kind: "remove",
        link: l
      })}>
              Remove
            </Button>
          </div>
  }];
}
function Detail({
  me,
  detail,
  onChange
}: {
  readonly me: Me;
  readonly detail: AdminOrganizationDetail;
  readonly onChange: (next: AdminOrganizationDetail) => void;
}) {
  const [change, setChange] = useState<Change | null>(null);
  const {
    organization
  } = detail;
  const canChange = me.staffRole === "superuser" || me.staffRole === "support";
  const active = detail.links.filter(l => l.status !== "revoked").length;
  const removed = detail.links.length - active;
  return <>
      <PageHead back={<BackToResults />} title={organization.name} meta={<>
            <span>
              {organization.members} {organization.members === 1 ? "member" : "members"}
            </span>
            <span>
              {active} linked {active === 1 ? "patient" : "patients"}
              {removed === 0 ? "" : `, ${removed} removed`}
            </span>
            <span>Slug {organization.slug ?? "none"}</span>
            <span>Created {day(organization.createdAt)}</span>
            <Truncate value={organization.id} />
          </>} />
      <Split side={<Context title="This clinic" who={organization.name} role={organization.slug ?? "No slug"} can={canChange ? "You can move or remove a link. You cannot approve a formula." : "You can read this clinic. You cannot move or remove a link."} links={detail.links.slice(0, 6).map(link => ({
      relation: "Patient",
      label: link.patientName ?? "Name not given",
      to: href.patient(link.patientId)
    }))} />} main={<div className="x78zum5 xdt5ytf x15agyyk">
            <Section title="Patient links" count={detail.links.length} info={<Info label="Share scope" text={FIELD_INFO.scope} />}>
              <Table label="Patient links" rows={detail.links} rowKey={l => l.id} empty="No patients are linked to this clinic." columns={linkColumns(canChange, setChange)} selected={l => l.id === change?.link.id} />
              {change === null ? null : <ChangePanel key={`${change.kind}-${change.link.id}`} change={change} clinicId={organization.id} onCancel={() => setChange(null)} onDone={next => {
          setChange(null);
          onChange(next);
        }} />}
            </Section>
            <Section title="Members" count={detail.members.length}>
              <Table label="Members" rows={detail.members} rowKey={m => m.userId} empty="No members yet." columns={[{
          label: "Name",
          cell: m => <Person name={m.name} email={m.email} to={href.user(m.userId)} />
        }, {
          label: "Email",
          cell: m => <Truncate value={m.email} />
        }, {
          label: "Role",
          nowrap: true,
          cell: m => CLINIC_ROLE[m.role]
        }, {
          label: "Licence",
          nowrap: true,
          phoneLabel: true,
          cell: m => m.licenceStatus === "verified" ? "Verified" : m.licenceStatus === "unverified" ? "Unverified" : "None"
        }, {
          label: "Joined",
          numeric: true,
          phoneLabel: true,
          cell: m => day(m.joinedAt)
        }]} />
            </Section>
            <Section title="Link changes" count={detail.history.length}>
              <History rows={detail.history} empty="No staff has moved or removed a link here." />
            </Section>
          </div>} />
    </>;
}
export function OrganizationDetail({
  id,
  me
}: {
  readonly id: string;
  readonly me: Me;
}) {
  const [loaded, replace] = useLoad(`organization-${id}`, () => api<AdminOrganizationDetail>(ROUTES.adminOrganization, {
    params: {
      organizationId: id
    }
  }));
  if (loaded.state === "loading") return <PageHead title="Loading clinic" />;
  if (loaded.state === "failed") return <>
        <PageHead back={<BackToResults />} title="Clinic" />
        <ErrorText>{refusal(loaded.error)}</ErrorText>
      </>;
  return <Detail me={me} detail={loaded.data} onChange={replace} />;
}